import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, X, ShoppingBag, Clock, TrendingUp, Tag, Sparkles } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useSettings } from "@/hooks/useSettings";
import { formatCurrency } from "@/lib/format";

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  category: string | null;
  on_sale: boolean | null;
  sale_price: number | null;
  featured: boolean | null;
  created_at: string;
}

const RECENT_KEY = "rs_recent_searches";
const TRENDING = ["Saree", "Three-piece", "Cotton", "Silk", "Eid"];

const PRICE_RANGES = [
  { label: "All", min: 0, max: Infinity },
  { label: "Under ৳1k", min: 0, max: 1000 },
  { label: "৳1k–3k", min: 1000, max: 3000 },
  { label: "৳3k+", min: 3000, max: Infinity },
];

const SORTS = [
  { label: "Popular", value: "popular" },
  { label: "Latest", value: "latest" },
];

// simple Levenshtein distance (capped to small inputs for "did you mean")
const levenshtein = (a: string, b: string) => {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m || !n) return Math.max(m, n);
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
};

const Highlight = ({ text, query }: { text: string; query: string }) => {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary/20 text-foreground rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
};

export const SearchFab = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCat, setActiveCat] = useState<string>("All");
  const [priceIdx, setPriceIdx] = useState(0);
  const [sort, setSort] = useState<string>("popular");
  const [recent, setRecent] = useState<string[]>([]);
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const navigate = useNavigate();
  const location = useLocation();
  const hideFab = /^\/(checkout|order-confirmation)/.test(location.pathname);

  // load recent searches once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setRecent(JSON.parse(raw));
    } catch {}
  }, []);

  // load products + categories when modal opens
  useEffect(() => {
    if (!open || products.length) return;
    supabase
      .from("products")
      .select("id,name,price,image_url,category,on_sale,sale_price,featured,created_at")
      .then(({ data }) => {
        const list = (data as Product[]) ?? [];
        setProducts(list);
        const cats = Array.from(
          new Set(list.map((p) => p.category).filter(Boolean) as string[]),
        );
        setCategories(cats);
      });
  }, [open, products.length]);

  const saveRecent = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    const next = [trimmed, ...recent.filter((r) => r.toLowerCase() !== trimmed.toLowerCase())].slice(0, 6);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
  };

  const clearRecent = () => {
    setRecent([]);
    try { localStorage.removeItem(RECENT_KEY); } catch {}
  };

  // matched products with category + partial word match
  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = products;
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q),
      );
    }
    if (activeCat !== "All") {
      list = list.filter((p) => p.category === activeCat);
    }
    const range = PRICE_RANGES[priceIdx];
    list = list.filter((p) => {
      const price = p.on_sale && p.sale_price ? Number(p.sale_price) : Number(p.price);
      return price >= range.min && price <= range.max;
    });
    if (sort === "latest") {
      list = [...list].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    } else {
      // popular = featured first, then on_sale
      list = [...list].sort((a, b) => Number(!!b.featured) - Number(!!a.featured) || Number(!!b.on_sale) - Number(!!a.on_sale));
    }
    return list;
  }, [query, products, activeCat, priceIdx, sort]);

  // matching category suggestions
  const categoryMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return categories.filter((c) => c.toLowerCase().includes(q)).slice(0, 4);
  }, [query, categories]);

  // did-you-mean suggestion
  const didYouMean = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q.length < 3 || matched.length > 0) return null;
    const pool = [
      ...categories,
      ...products.flatMap((p) => p.name.split(/\s+/).filter((w) => w.length >= 3)),
    ];
    let best: { word: string; dist: number } | null = null;
    for (const w of pool) {
      const lw = w.toLowerCase();
      if (lw === q) continue;
      const d = levenshtein(q, lw);
      if (d > 0 && d <= 2 && (!best || d < best.dist)) best = { word: w, dist: d };
    }
    return best?.word ?? null;
  }, [query, products, categories, matched.length]);

  const goSearch = (q: string) => {
    if (!q.trim()) return;
    saveRecent(q);
    navigate(`/?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    goSearch(query);
  };

  const showResults = !!query.trim() || activeCat !== "All" || priceIdx !== 0;
  const visible = matched.slice(0, 8);

  return (
    <>
      {!hideFab && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Search products"
          title="Search"
          style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
          className="fixed right-4 md:right-6 z-40 grid h-12 w-12 md:h-14 md:w-14 place-items-center rounded-full bg-gradient-primary text-primary-foreground shadow-card hover:scale-105 active:scale-95 transition-smooth"
        >
          <Search className="h-5 w-5 md:h-6 md:w-6" />
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl p-0 gap-0 top-[8%] translate-y-0 overflow-hidden">
          <form onSubmit={handleSubmit} className="relative border-b">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, categories..."
              className="pl-14 pr-12 h-14 text-base border-0 rounded-none focus-visible:ring-0"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </form>

          {/* Quick filters */}
          <div className="flex flex-wrap items-center gap-1.5 border-b px-3 py-2">
            <div className="flex items-center gap-1 overflow-x-auto">
              <button
                onClick={() => setActiveCat("All")}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-smooth ${
                  activeCat === "All" ? "bg-foreground text-background" : "bg-secondary hover:bg-secondary/70"
                }`}
              >
                All
              </button>
              {categories.slice(0, 6).map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-smooth ${
                    activeCat === c ? "bg-foreground text-background" : "bg-secondary hover:bg-secondary/70"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <span className="mx-1 hidden h-4 w-px bg-border sm:inline-block" />
            <div className="flex items-center gap-1">
              {PRICE_RANGES.map((r, i) => (
                <button
                  key={r.label}
                  onClick={() => setPriceIdx(i)}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-smooth ${
                    priceIdx === i ? "bg-primary/15 text-primary" : "bg-secondary hover:bg-secondary/70"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <span className="mx-1 hidden h-4 w-px bg-border sm:inline-block" />
            <div className="flex items-center gap-1">
              {SORTS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSort(s.value)}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-smooth ${
                    sort === s.value ? "bg-accent/15 text-accent" : "bg-secondary hover:bg-secondary/70"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-2 animate-fade-up">
            {/* Empty state: trending + recent */}
            {!showResults && (
              <div className="space-y-4 p-3">
                {recent.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center justify-between px-1">
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" /> Recent
                      </p>
                      <button
                        onClick={clearRecent}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {recent.map((r) => (
                        <button
                          key={r}
                          onClick={() => setQuery(r)}
                          className="rounded-full bg-secondary px-3 py-1.5 text-sm hover:bg-secondary/70 transition-smooth"
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="mb-2 flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <TrendingUp className="h-3.5 w-3.5" /> Trending
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {TRENDING.map((t) => (
                      <button
                        key={t}
                        onClick={() => setQuery(t)}
                        className="rounded-full border border-border bg-background px-3 py-1.5 text-sm hover:border-primary/50 hover:text-primary transition-smooth"
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Category suggestions */}
            {showResults && categoryMatches.length > 0 && (
              <div className="px-3 pt-2 pb-1">
                <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Tag className="h-3 w-3" /> Categories
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {categoryMatches.map((c) => (
                    <button
                      key={c}
                      onClick={() => {
                        saveRecent(c);
                        navigate(`/?cat=${encodeURIComponent(c)}`);
                        setOpen(false);
                      }}
                      className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/15 transition-smooth"
                    >
                      <Highlight text={c} query={query} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No results */}
            {showResults && visible.length === 0 && (
              <div className="p-8 text-center">
                <Sparkles className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
                <p className="text-sm font-medium">No products match {query ? `"${query}"` : "these filters"}.</p>
                {didYouMean && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Did you mean{" "}
                    <button
                      onClick={() => setQuery(didYouMean)}
                      className="font-medium text-primary hover:underline"
                    >
                      {didYouMean}
                    </button>
                    ?
                  </p>
                )}
                <div className="mt-4 flex flex-wrap justify-center gap-1.5">
                  {TRENDING.slice(0, 4).map((t) => (
                    <button
                      key={t}
                      onClick={() => setQuery(t)}
                      className="rounded-full bg-secondary px-3 py-1 text-xs hover:bg-secondary/70"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Product results */}
            {visible.length > 0 && (
              <div className="px-1 pt-1">
                {showResults && (
                  <p className="mb-1 px-3 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Products
                  </p>
                )}
                {visible.map((p) => {
                  const price = p.on_sale && p.sale_price ? Number(p.sale_price) : Number(p.price);
                  return (
                    <Link
                      key={p.id}
                      to={`/product/${p.id}`}
                      onClick={() => { saveRecent(query || p.name); setOpen(false); }}
                      className="flex items-center gap-3 rounded-xl p-2 hover:bg-secondary transition-smooth"
                    >
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-secondary">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="grid h-full w-full place-items-center">
                            <ShoppingBag className="h-5 w-5 text-muted-foreground/40" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">
                          <Highlight text={p.name} query={query} />
                        </p>
                        {p.category && (
                          <p className="text-xs text-muted-foreground">
                            <Highlight text={p.category} query={query} />
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        {p.on_sale && p.sale_price ? (
                          <>
                            <span className="font-display font-semibold text-destructive block">
                              {formatCurrency(price, symbol)}
                            </span>
                            <span className="text-xs text-muted-foreground line-through">
                              {formatCurrency(Number(p.price), symbol)}
                            </span>
                          </>
                        ) : (
                          <span className="font-display font-semibold">
                            {formatCurrency(price, symbol)}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
                {matched.length > visible.length && (
                  <button
                    onClick={() => goSearch(query)}
                    className="w-full rounded-xl p-2.5 text-center text-sm font-medium text-primary hover:bg-secondary transition-smooth"
                  >
                    See all {matched.length} results →
                  </button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
